import { generateAuthHeaders } from './auth.ts';
import type {
  WiroClientOptions,
  WiroFileParam,
  RunResponse,
  TaskDetailResponse,
  TaskDetailRequest,
  KillTaskResponse,
  KillTaskRequest,
  CancelTaskResponse,
  CancelTaskRequest,
} from './types/index.ts';

/**
 * Client library for interacting with the Wiro AI API.
 *
 * This client provides methods to run AI models, check task status, and manage tasks.
 * It uses Bun's native fetch API for HTTP requests and supports file uploads via FormData.
 *
 * @example
 * ```ts
 * import { WiroClient } from 'wiro-ai-sdk';
 *
 * const client = new WiroClient({ 
 *   apiKey: 'your_key', 
 *   apiSecret: 'your_secret' 
 * });
 * 
 * // Run a model with parameters
 * const runResult = await client.run('wiro', 'professional-headshot', {
 *   background: 'neutral',
 *   outputFormat: 'jpeg',
 *   callbackUrl: 'https://example.com/callback'
 * });
 *
 * // Get task details
 * const taskId = runResult.taskid!;
 * const detail = await client.getTaskDetail({ taskid: taskId });
 * console.log('Task status:', detail.tasklist[0]?.status);
 * 
 * // Run a model with file upload
 * const runWithFile = await client.run('wiro', 'polaroid-effect', {
 *   effectType: 'polaroid_smile',
 *   seed: 42,
 * }, [
 *   { name: 'inputImage', file: '/path/to/image.jpg' }
 * ]);
 * ```
 */
export class WiroClient {
  private readonly apiKey: string;
  private readonly apiSecret: string;
  private readonly baseUrl: string;

  constructor(options: WiroClientOptions) {
    const { apiKey, apiSecret, baseUrl = 'https://api.wiro.ai/v1' } = options;

    if (!apiKey) {
      throw new Error('WiroClient requires an apiKey');
    }
    if (!apiSecret) {
      throw new Error('WiroClient requires an apiSecret');
    }

    // Validate baseUrl
    if (baseUrl && !baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      throw new Error('Invalid baseUrl: must start with http:// or https://');
    }

    this.apiKey = apiKey;
    this.apiSecret = apiSecret;
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  /**
   * Run a Wiro AI model. You must provide the owner (e.g. "wiro") and model slug
   * (e.g. "professional-headshot"). Parameters specific to the model can be passed as
   * an object. Files can optionally be attached; supply them via the `files` array.
   *
   * @param owner - The owner/namespace of the model (e.g., "wiro")
   * @param model - The model slug/name (e.g., "professional-headshot")
   * @param params - Object representing JSON parameters accepted by the model
   * @param files - Optional array of file parameters. Each file will be appended to a
   *                multipart form under its `name` property.
   * @returns Promise resolving to the API response with taskid and socketaccesstoken
   * 
   * @example
   * ```ts
   * // Run without files
   * const result = await client.run('wiro', 'professional-headshot', {
   *   inputImageUrl: 'https://example.com/photo.jpg',
   *   background: 'neutral'
   * });
   * 
   * // Run with file upload
   * const result = await client.run('wiro', 'polaroid-effect', {
   *   effectType: 'polaroid_smile'
   * }, [
   *   { name: 'inputImage', file: './photo.jpg' }
   * ]);
   * ```
   */
  async run<T = any>(
    owner: string,
    model: string,
    params: Record<string, any>,
    files?: WiroFileParam[]
  ): Promise<RunResponse & { data?: T }> {
    const authHeaders = generateAuthHeaders(this.apiKey, this.apiSecret);
    const url = `${this.baseUrl}/Run/${owner}/${model}`;

    let body: FormData | string;
    let headers: Record<string, string>;

    // When files are present, construct a FormData body. Otherwise send JSON.
    if (files && files.length > 0) {
      const form = new FormData();
      
      // Append provided parameters to the form
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          
          if (Array.isArray(value)) {
            // FormData supports duplicate keys for arrays
            value.forEach(v => form.append(key, String(v)));
          } else {
            form.append(key, String(value));
          }
        }
      }
      
      // Append files to the form
      for (const fileParam of files) {
        let fileData: Blob | File;
        let filename = fileParam.filename;

        // If the file is a string, treat it as a path and read using Bun.file()
        if (typeof fileParam.file === 'string') {
          try {
            const bunFile = Bun.file(fileParam.file);
            fileData = bunFile;

            // Extract filename from path if not provided
            if (!filename) {
              const pathParts = fileParam.file.split(/[/\\]/);
              filename = pathParts[pathParts.length - 1] || fileParam.name;
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            throw new Error(`Failed to read file at "${fileParam.file}": ${errorMsg}`);
          }
        } else {
          // File is already a Blob or File
          fileData = fileParam.file;

          // Use the File's name if available, otherwise generate one
          if (!filename) {
            if ('name' in fileData && fileData.name) {
              filename = fileData.name;
            } else {
              filename = `${fileParam.name}.bin`;
            }
          }
        }

        form.append(fileParam.name, fileData, filename);
      }
      
      body = form;
      // Don't set Content-Type - let fetch set it with the boundary
      headers = { ...authHeaders };
    } else {
      // Send parameters as JSON
      body = JSON.stringify(params);
      headers = {
        ...authHeaders,
        'Content-Type': 'application/json',
      };
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Wiro API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json() as Promise<RunResponse & { data?: T }>;
  }

  /**
   * Retrieve details about one or more tasks. You can supply either a task ID or a
   * socket access token. At least one of these properties must be provided.
   *
   * @param taskInfo - An object containing either a `taskid` or `tasktoken` field
   * @returns Promise resolving to task details including status and outputs
   * 
   * @example
   * ```ts
   * // Get task by ID
   * const detail = await client.getTaskDetail({ taskid: '2221' });
   * 
   * // Get task by token
   * const detail = await client.getTaskDetail({ 
   *   tasktoken: 'eDcCm5yyUfIvMFspTwww49OUfgXkQt' 
   * });
   * 
   * // Check task status
   * const task = detail.tasklist[0];
   * if (task.status === 'task_postprocess_end') {
   *   console.log('Task completed!', task.outputs);
   * }
   * ```
   */
  async getTaskDetail<T = any>(taskInfo: TaskDetailRequest): Promise<TaskDetailResponse<T>> {
    if (!taskInfo.taskid && !taskInfo.tasktoken) {
      throw new Error('getTaskDetail requires either a taskid or a tasktoken');
    }

    const authHeaders = generateAuthHeaders(this.apiKey, this.apiSecret);
    const url = `${this.baseUrl}/Task/Detail`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(taskInfo),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Wiro API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json() as Promise<TaskDetailResponse<T>>;
  }

  /**
   * Kill a running task. Provide either the task ID or the socket access token.
   * Use this to terminate tasks that are currently executing.
   *
   * @param taskInfo - An object containing either `taskid` or `tasktoken`
   * @returns Promise resolving to the kill task response
   * 
   * @example
   * ```ts
   * // Kill by task ID
   * await client.killTask({ taskid: '534574' });
   * 
   * // Kill by token
   * await client.killTask({ 
   *   tasktoken: 'ZpYote30on42O4jjHXNiKmrWAZqbRE' 
   * });
   * ```
   */
  async killTask(taskInfo: KillTaskRequest): Promise<KillTaskResponse> {
    if (!taskInfo.taskid && !taskInfo.tasktoken) {
      throw new Error('killTask requires either a taskid or a tasktoken');
    }

    const authHeaders = generateAuthHeaders(this.apiKey, this.apiSecret);
    const url = `${this.baseUrl}/Task/Kill`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(taskInfo),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Wiro API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json() as Promise<KillTaskResponse>;
  }

  /**
   * Cancel a queued task by its task ID. Use this for tasks that are waiting in the queue
   * and have not started execution yet.
   *
   * @param taskid - The task ID to cancel
   * @returns Promise resolving to the cancel task response
   * 
   * @example
   * ```ts
   * await client.cancelTask('634574');
   * ```
   */
  async cancelTask(taskid: string): Promise<CancelTaskResponse> {
    const authHeaders = generateAuthHeaders(this.apiKey, this.apiSecret);
    const url = `${this.baseUrl}/Task/Cancel`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ taskid }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Wiro API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json() as Promise<CancelTaskResponse>;
  }
}
